import { ChangeDetectionStrategy, Component, Inject, ViewChild } from '@angular/core';
import { NgForm, Validators } from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSelectionListChange} from '@angular/material/list';
import {PageEvent} from '@angular/material/paginator';
import { NgxSpinnerService } from 'ngx-spinner';

import {BehaviorSubject, combineLatest, firstValueFrom, throwError} from 'rxjs';
import { catchError, delay, filter, first, map, switchMap, tap } from 'rxjs/operators';

import { required } from '@atmi/core/utils';
import { FormManagerService } from '@sim/form';
import { makeQueryBuilder, makeQueryRules, TableFilterType } from '@ui/ui.module';

import { CollectionFiltersComponent } from '../../components/collection-filters/collection-filters.component';
import { Collection, CollectionFilter, CollectionFilterMeta } from '../../models/collection';
import { CollectionApiService } from '../../services/collection-api.service';

export interface CollectionPickerDialogData {
  selection?: 'single' | 'multiple';
}

const PAGINATOR = { length: 12, pageIndex: 0, pageSize: 12 };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-collection-picker-dialog',
  styleUrls: ['./collection-picker-dialog.component.scss'],
  templateUrl: './collection-picker-dialog.component.html',
})
export class CollectionPickerDialogComponent {

  isLoading = false;
  selectedCollection: Collection[] = [];

  readonly MobileFiltersComponent = CollectionFiltersComponent;

  private readonly paginator$ = new BehaviorSubject<PageEvent | undefined>(PAGINATOR);
  private readonly filters$ = new BehaviorSubject<TableFilterType | undefined>(undefined);

  readonly collectionPage$ = combineLatest([
    this.paginator$.pipe(filter((paginator): paginator is PageEvent => !!paginator)),
    this.filters$,
  ]).pipe(
    switchMap(([paginator, filters]) =>
      this.collectionApi.index({
        filter: filters,
        limit: paginator.pageSize,
        page: paginator.pageIndex + 1,
        sort: [{ type: 'desc', field: 'createdAt' }],
      }),
    ),
    map((collectionPage) => {
      const data = collectionPage.data.map((m) => ({ ...m }));
      return { ...collectionPage, data };
    }),
    delay(500),
    tap(() => this.stopLoadingUi()),
    catchError((e) => {
      this.stopLoadingUi();

      return throwError(e);
    }),
  );

  readonly pageSizeOptions = [12, 24, 48];

  readonly newCollectionForm = this.formService.makeForm<Collection>({
    titolo: { value: undefined },
  });

  // Needs to https://github.com/angular/components/issues/7522
  @ViewChild('newCollectionFormDirective') private readonly newCollectionFormDirective?: NgForm;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: CollectionPickerDialogData,
    private readonly collectionApi: CollectionApiService,
    private readonly dialogRef: MatDialogRef<CollectionPickerDialogComponent>,
    private readonly formService: FormManagerService,
    private readonly spinnerService: NgxSpinnerService,
  ) {
    this.data = {
      selection: 'multiple',
      ...this.data,
    };

    this.loadingUi();
  }

  async addCollectionHandler() {
    if (this.newCollectionForm.invalid) {
      this.newCollectionForm.markAllAsTouched();
      return;
    }
    const { titolo } = this.newCollectionForm.value;

    await firstValueFrom(this.collectionApi.post({ titolo }).pipe(first()));

    this.newCollectionFormDirective?.resetForm();
    this.newCollectionForm.reset();
    this.newCollectionForm.markAsUntouched();

    required(this.paginator$.value);
    this.paginator$.next({ ...this.paginator$.value, pageIndex: 0 });
  }

  toggleCollectionHandler(event: MatSelectionListChange) {
    const collection = event.options[0].value;
    if (this.selectedCollection.find((c) => c.id === collection.id)) {
      this.selectedCollection = this.selectedCollection.filter((m) => m.id !== collection.id);
    } else {
      this.selectedCollection = [...this.selectedCollection, collection];
    }
  }

  isCollectionSelected(collection: Collection) {
    return this.selectedCollection.find((m) => m.id === collection.id);
  }

  changePageHandler(pageEvent: PageEvent) {
    this.loadingUi();
    this.paginator$.next(pageEvent);
  }

  closeHandler() {
    this.dialogRef.close();
  }

  saveHandler() {
    this.dialogRef.close(this.selectedCollection);
  }

  applyFilterWithDelayHandler(filters: CollectionFilter) {
    const rules = makeQueryRules(filters, CollectionFilterMeta);
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }

  private loadingUi() {
    this.spinnerService.show('loadingCollection');
    this.isLoading = true;
  }

  private stopLoadingUi() {
    this.spinnerService.hide('loadingCollection');
    this.isLoading = false;
  }
}
